import { useCallback, useMemo } from 'react';
import { useNotify } from '@/feature/notifications';
import { tryDecodeError, isValidationError, getValidationErrorMessage, DecodedScError } from '../utils/decodeScError';
import { getAccountErrorInfo, getOrderErrorInfo, ErrorInfo } from '../features/trading';
import { OrderStatusReason, OrderStatusReasonUnspecified } from '@/api/spec';
import { captureError, isUserRejection } from '../utils/sentryHelpers';

export interface ReasonHandlerResult<TReason> extends ErrorInfo {
  reason?: TReason;
}

export interface ReasonHandler<TReason> {
  defaultReason?: TReason;
  setFailed?: (reason: TReason, message: string) => void;
  getErrorInfo: (decoded: DecodedScError) => ReasonHandlerResult<TReason>;
}

interface ErrorHandlerOptions<TReason = unknown> {
  reasonHandler?: ReasonHandler<TReason>;

  // For general operations that use notifications
  useNotifications?: boolean;
  notify?: (message: string, severity?: 'error' | 'warning' | 'info') => void;

  // Custom fallback messages
  fallbackMessage?: string;
  context?: string; // e.g., 'deposit', 'withdrawal', 'order placement'
}

interface ErrorHandlerResult {
  handleError: (error: unknown) => void;
}

/**
 * Unified error handling hook that provides consistent error processing
 * across all contract interaction hooks
 */
export function useErrorHandler<TReason>(options: ErrorHandlerOptions<TReason> = {}): ErrorHandlerResult {
  const notify = useNotify();

  const { reasonHandler, useNotifications = true, notify: notifyOverride, fallbackMessage, context = 'operation' } = options;

  const notifyFn = notifyOverride ?? notify;

  const handleError = useCallback(
    (error: unknown) => {
      console.error(`${context} error:`, error);

      // Check if it's a validation error first
      if (isValidationError(error)) {
        const validationMessage = getValidationErrorMessage(error);
        const message = validationMessage || `Invalid input provided. Please check your inputs and try again.`;

        if (reasonHandler?.setFailed && reasonHandler.defaultReason !== undefined) {
          reasonHandler.setFailed(reasonHandler.defaultReason, message);
        } else if (useNotifications) {
          notifyFn(message, 'error');
        }
        return;
      }

      // Try to decode smart contract revert (tries multiple approaches)
      const decoded = tryDecodeError(error);

      if (decoded) {
        if (reasonHandler) {
          const { reason, message, severity } = reasonHandler.getErrorInfo(decoded);

          if (reason !== undefined && reasonHandler.setFailed) {
            reasonHandler.setFailed(reason, message);
          }

          if (useNotifications) {
            notifyFn(message, severity);
          }
        } else if (useNotifications) {
          notifyFn(decoded.errorName ?? 'Unknown contract error', 'error');
        }
        return;
      }

      // User rejected the transaction in their wallet — show warning, not error
      if (isUserRejection(error)) {
        const rejectionMessage = `Transaction not completed. You can try again when ready.`;
        if (reasonHandler?.setFailed && reasonHandler.defaultReason !== undefined) {
          reasonHandler.setFailed(reasonHandler.defaultReason, rejectionMessage);
        } else if (useNotifications) {
          notifyFn(rejectionMessage, 'warning');
        }
        return;
      }

      // Fallback to generic error handling — report unexpected errors to Sentry
      captureError(error, context);

      const errorMessage =
        error instanceof Error
          ? error.message
          : fallbackMessage || `Failed to prepare ${context}. Please check your inputs and try again.`;

      if (reasonHandler?.setFailed && reasonHandler.defaultReason !== undefined) {
        reasonHandler.setFailed(reasonHandler.defaultReason, errorMessage);
      } else if (useNotifications) {
        notifyFn(errorMessage, 'error');
      }
    },
    [context, fallbackMessage, notifyFn, reasonHandler, useNotifications]
  );

  return { handleError };
}

function createOrderReasonHandler(
  setFailed?: (reason: OrderStatusReason, message: string) => void
): ReasonHandler<OrderStatusReason> {
  return {
    defaultReason: OrderStatusReasonUnspecified,
    setFailed,
    getErrorInfo: (decoded) => {
      const errorInfo = getOrderErrorInfo(decoded.orderStatusReason, decoded.argsFormatted);
      return { ...errorInfo, reason: decoded.orderStatusReason };
    },
  };
}

export const orderNotificationReasonHandler = createOrderReasonHandler();

export const accountReasonHandler: ReasonHandler<string> = {
  getErrorInfo: (decoded) => getAccountErrorInfo(decoded.errorName, decoded.argsFormatted),
};

/**
 * Specialized error handler for trading operations that use OrderStatusReason
 */
export function useTradingErrorHandler(
  setFailed: (reason: OrderStatusReason, message: string) => void,
  context: string = 'trading operation'
): ErrorHandlerResult {
  const reasonHandler = useMemo(() => createOrderReasonHandler(setFailed), [setFailed]);

  return useErrorHandler({
    reasonHandler,
    useNotifications: false,
    context,
  });
}

/**
 * Specialized error handler for account operations that use notifications
 */
export function useAccountErrorHandler(context: string = 'account operation'): ErrorHandlerResult {
  return useErrorHandler({
    reasonHandler: accountReasonHandler,
    useNotifications: true,
    fallbackMessage: `Failed to complete ${context}. Please try again.`,
    context,
  });
}

/**
 * Handles a decoded smart contract error directly (for use in transaction monitoring)
 * This is useful when you already have a DecodedScError from reverted transactions
 */
export function handleDecodedError(
  decoded: DecodedScError | null,
  fallbackMessage: string,
  notify: (message: string, severity: 'success' | 'info' | 'warning' | 'error', options?: { debugInfo?: string }) => void,
  reasonHandler?: ReasonHandler<any>,
  debugInfo?: string
): void {
  if (decoded) {
    if (reasonHandler) {
      const { message, severity } = reasonHandler.getErrorInfo(decoded);
      notify(message, severity, { debugInfo });
      return;
    }

    notify(decoded.errorName ?? fallbackMessage, 'error', { debugInfo });
  } else {
    notify(fallbackMessage, 'error', { debugInfo });
  }
}
